import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TableReservationNotice } from "../../../../../api/bills";
import { getApiErrorMessage } from "../../../../../utils/translations";
import type { MessageInstance } from "antd/es/message/interface";
import { checkNewTableName, hasApiDetail } from "../manage/newTableNameCheck";

interface OpenSchemaTableOptions {
  /** Схемата е на екрана (режим „Схема" + изгледът с масите). Стане ли
   *  false — изход от режима или меню, отворено по друг път („Настани",
   *  глас, „Добави") — висящото отваряне се обезсилва. */
  active: boolean;
  /** „Брой гости" преди „Нова маса" — „Отказ" спира всичко. */
  askBeforeNewTable: () => Promise<boolean>;
  /** Модалът „Масата има резервация — продължаваш ли?" на `Ordering`
   *  (размонтиран екран би отказал висящия въпрос). */
  confirmNotice: (name: string, notice: TableReservationNotice) => Promise<boolean>;
  message: MessageInstance;
  /** Влиза в менюто за новата маса (`handleNewTableSubmit`). */
  onOpen: (name: string) => void;
}

/**
 * „Нова маса" за свободна маса от схемата — СЪЩИТЕ стъпки като модала с името,
 * само без клавиатурата: „Брой гости" → проверката на името (заета, резервация,
 * счупен маршрут — `checkNewTableName`) → менюто. Сметката се създава чак при
 * „Поръчай", както досега. Заета от друг терминал маса → съобщение и пресни
 * сметки (сметката ѝ се избира сама, ако е видима).
 *
 * Последният избор печели: всяко отваряне получава пореден номер, а след
 * всяко чакане (въпрос, проверка в облака) продължава само последното.
 * По-ново отваряне, `cancelPendingOpen` (нов избор в схемата), схема извън
 * екрана (`active` false — изход от режима или меню, отворено по друг път)
 * или затворен екран обезсилват висящото — късният отговор за маса A не
 * вкарва в менюто на A, след като операторът вече е на B или в менюто на
 * друга сметка; не излиза и въпросът за резервацията на A, нито грешката ѝ.
 * Проверките за заета маса и резервация остават за последното отваряне.
 */
export const useOpenSchemaTable = ({
  active,
  askBeforeNewTable,
  confirmNotice,
  message,
  onOpen,
}: OpenSchemaTableOptions) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const latestRequestRef = useRef(0);

  const cancelPendingOpen = useCallback(() => {
    latestRequestRef.current += 1;
  }, []);
  useEffect(() => cancelPendingOpen, [cancelPendingOpen]);
  useEffect(() => {
    if (!active) cancelPendingOpen();
  }, [active, cancelPendingOpen]);

  const openSchemaTable = async (name: string) => {
    latestRequestRef.current += 1;
    const requestId = latestRequestRef.current;
    const superseded = () => requestId !== latestRequestRef.current;

    if (!(await askBeforeNewTable()) || superseded()) return;
    const outcome = await checkNewTableName(name, (tableName, notice) =>
      superseded() ? Promise.resolve(false) : confirmNotice(tableName, notice),
    );
    if (superseded() || outcome.kind === "declined") return;
    if (outcome.kind === "exists") {
      message.error(t("ordering.tableAlreadyExists", { name }));
      void queryClient.invalidateQueries({ queryKey: ["bills"] });
      return;
    }
    if (outcome.kind === "failed") {
      const offlineError = t("ordering.tableCheckOffline");
      message.error(
        hasApiDetail(outcome.error)
          ? getApiErrorMessage(outcome.error, offlineError)
          : offlineError,
      );
      return;
    }
    if (outcome.skippedReason !== null) {
      message.warning(t("ordering.tableCheckSkipped", { reason: outcome.skippedReason }));
    }
    onOpen(name);
  };

  return { openSchemaTable, cancelPendingOpen };
};
